// apps/web/src/components/astral/AstralAppearanceDrawer.tsx
import { X, Network, GitFork, List, Palette, Check } from "lucide-react";
import type { AstralLayout, AstralThemeId, AstralNodeShape } from "../../types/astral";
import { ASTRAL_THEMES } from "../../data/astralThemes";

interface AstralAppearanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  layout: AstralLayout;
  onSelectLayout: (layout: AstralLayout) => void;
  theme: AstralThemeId;
  onSelectTheme: (theme: AstralThemeId) => void;
  shape?: AstralNodeShape;
  onSelectShape?: (shape: AstralNodeShape) => void;
}

export function AstralAppearanceDrawer({
  isOpen,
  onClose,
  layout,
  onSelectLayout,
  theme,
  onSelectTheme,
  shape = "rounded",
  onSelectShape,
}: AstralAppearanceDrawerProps) {
  if (!isOpen) return null;

  const layoutOptions: { id: AstralLayout; label: string; icon: any; desc: string }[] = [
    {
      id: "map",
      label: "Mapa mental",
      icon: Network,
      desc: "Ramificação horizontal com curvas orgânicas",
    },
    {
      id: "org",
      label: "Organograma",
      icon: GitFork,
      desc: "Estrutura hierárquica em árvore top-down",
    },
    {
      id: "list",
      label: "Lista",
      icon: List,
      desc: "Outline linear com recuos estruturados",
    },
  ];

  const shapeOptions: { id: AstralNodeShape; label: string; desc: string; previewRadius: string; hasLeftBorder?: boolean; isUnderline?: boolean }[] = [
    { id: "rounded", label: "Arredondado", desc: "Suave e moderno (padrão Meister)", previewRadius: "8px" },
    { id: "rectangle", label: "Retângulo", desc: "Reto executivo e corporativo", previewRadius: "2px" },
    { id: "underline", label: "Sublinhado", desc: "Minimalista clássico Tony Buzan", previewRadius: "0px", isUnderline: true },
    { id: "bubble", label: "Cápsula", desc: "Pílula oval 100% arredondada", previewRadius: "9999px" },
    { id: "card", label: "Notion Card", desc: "Card estruturado com destaque", previewRadius: "6px", hasLeftBorder: true },
  ];

  const themesList = Object.values(ASTRAL_THEMES);

  return (
    <aside
      style={{
        position: "absolute",
        top: "60px",
        left: "16px",
        width: "330px",
        maxHeight: "calc(100vh - 120px)",
        background: "rgba(17, 22, 34, 0.95)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: "16px",
        boxShadow: "0 20px 45px rgba(0, 0, 0, 0.65)",
        zIndex: 50,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        animation: "slideInLeft 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 16px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          background: "rgba(255, 255, 255, 0.02)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Palette size={16} style={{ color: "var(--apple-blue)" }} />
          <strong style={{ fontSize: "14px", color: "var(--ink)", fontWeight: 600 }}>
            Aparência & Estilos
          </strong>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "28px",
            height: "28px",
            minWidth: "28px",
            borderRadius: "50%",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            background: "rgba(255, 255, 255, 0.05)",
            color: "var(--ink-muted)",
            cursor: "pointer",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
          title="Fechar painel de aparência"
        >
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: "16px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Seção 1: Layout */}
        <div>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--ink-muted)",
              display: "block",
              marginBottom: "10px",
            }}
          >
            Estrutura & Layout
          </span>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
            {layoutOptions.map((opt) => {
              const Icon = opt.icon;
              const isSelected = layout === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onSelectLayout(opt.id)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    padding: "12px 6px",
                    borderRadius: "10px",
                    border: isSelected ? "2px solid var(--apple-blue)" : "1px solid rgba(255, 255, 255, 0.08)",
                    background: isSelected ? "rgba(56, 139, 253, 0.15)" : "rgba(255, 255, 255, 0.03)",
                    color: isSelected ? "#fff" : "var(--ink-soft)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title={opt.desc}
                >
                  <Icon size={18} style={{ color: isSelected ? "var(--apple-blue)" : "var(--ink-muted)" }} />
                  <span style={{ fontSize: "11px", fontWeight: isSelected ? 600 : 500 }}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Seção 2: Formatos dos Cards (MindMeister Shapes) */}
        {onSelectShape && (
          <div>
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--ink-muted)",
                display: "block",
                marginBottom: "10px",
              }}
            >
              Formato dos Cards
            </span>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
              {shapeOptions.map((shp) => {
                const isSelected = shape === shp.id;
                return (
                  <button
                    key={shp.id}
                    type="button"
                    onClick={() => onSelectShape(shp.id)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      border: isSelected ? "2px solid var(--apple-blue)" : "1px solid rgba(255, 255, 255, 0.08)",
                      background: isSelected ? "rgba(56, 139, 253, 0.12)" : "rgba(255, 255, 255, 0.03)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      textAlign: "left",
                      transition: "all 0.15s ease",
                    }}
                    title={shp.desc}
                  >
                    {/* Miniatura representativa do formato do card */}
                    <div
                      style={{
                        width: "28px",
                        height: "16px",
                        backgroundColor: shp.isUnderline ? "transparent" : "rgba(56, 139, 253, 0.25)",
                        border: shp.isUnderline ? "none" : "1.5px solid var(--apple-blue)",
                        borderBottom: shp.isUnderline ? "2.5px solid var(--apple-blue)" : undefined,
                        borderLeft: shp.hasLeftBorder ? "3.5px solid var(--apple-blue)" : undefined,
                        borderRadius: shp.previewRadius,
                        flexShrink: 0,
                      }}
                    />

                    <span
                      style={{
                        fontSize: "11.5px",
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? "#fff" : "var(--ink-soft)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {shp.label}
                    </span>

                    {isSelected && (
                      <Check size={12} style={{ color: "var(--apple-blue)", marginLeft: "auto", flexShrink: 0 }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Seção 3: Temas */}
        <div>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--ink-muted)",
              display: "block",
              marginBottom: "10px",
            }}
          >
            Temas Visuais
          </span>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
            {themesList.map((th) => {
              const isSelected = theme === th.id;
              return (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => onSelectTheme(th.id)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    border: isSelected ? "2px solid var(--apple-blue)" : "1px solid rgba(255, 255, 255, 0.08)",
                    background: th.bgCanvas,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                    position: "relative",
                  }}
                >
                  {/* Visual preview strip */}
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "3px",
                        backgroundColor: th.nodeRootBg,
                      }}
                    />
                    <div style={{ display: "flex", gap: "2px", flex: 1 }}>
                      {th.accentColors.slice(0, 4).map((acc, idx) => (
                        <div
                          key={idx}
                          style={{
                            height: "6px",
                            flex: 1,
                            borderRadius: "2px",
                            backgroundColor: acc,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: th.nodeLevel1Text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {th.name.split(" ")[0]}
                    </span>
                    {isSelected && (
                      <Check size={12} style={{ color: "var(--apple-blue)", flexShrink: 0 }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}
